<!-- MedAdherence_Patients_List.vue -->
<template>
  <div class="flex flex-col">
    <!-- Filter Bar -->
    <Worklist_FilterBar
      storage-key="med_adherence_entity_filter"
      :patient-search="true"
      @filter-changed="onEntityFilterChanged"
    >
      <template #filters>
        <div style="width: 200px">
          <SimpleMultiSelect
            v-model="selectedPharmacies"
            :options="pharmacyOptions"
            placeholder="Pharmacy"
            :searchable="true"
            @change="applyPharmacyFilter"
          />
        </div>
        <button
          @click="toggleFavoritesFilter"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            favoritesFilterActive
              ? 'bg-yellow-500 text-white border-yellow-500'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'star']" class="mr-1" />
          My Favorites
        </button>
        <button
          @click="toggleShowSnoozed"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            showSnoozed
              ? 'bg-yellow-600 text-white border-yellow-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'clock']" class="mr-1" />
          {{ showSnoozed ? 'Showing Snoozed' : 'Show Snoozed' }}
        </button>
        <button
          @click="toggleShowExpired"
          :title="'Show patients who dropped off the payer\'s latest file (expired, history preserved)'"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            showExpired
              ? 'bg-gray-600 text-white border-gray-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'box-archive']" class="mr-1" />
          {{ showExpired ? 'Showing Expired' : 'Show Expired' }}
        </button>
      </template>
    </Worklist_FilterBar>

    <!-- Toolbar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <h2 class="text-lg font-semibold text-gray-900">Medication Adherence</h2>
        <span class="text-sm text-gray-500">
          {{ stats.totalPatients }} patients
        </span>
      </div>

      <div class="flex items-center gap-3">
        <ExportMenu :exports="[{ type: 'csv', action: exportData }]" />
        <div class="flex items-center text-xs text-gray-600">
          <span class="font-medium text-orange-600">{{ stats.openGaps }}</span>
          <span class="ml-1">open gaps</span>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left: AG-Grid List -->
      <div
        :style="{ width: selectedPatient ? '50%' : '100%' }"
        class="transition-all duration-300 flex flex-col border-r border-gray-200"
        style="height: calc(100vh - 200px)"
      >
        <!-- AG-Grid -->
        <AgGridVue
          :key="tableKey"
          :theme="gridTheme"
          class="flex-1 w-full"
          :column-defs="computedColumnDefs"
          :default-col-def="defaultColDef"
          :grid-options="gridOptions"
          :loading="loading"
          :row-height="80"
          :header-height="48"
          :row-model-type="'serverSide'"
          :get-row-id="getRowId"
          :row-class-rules="rowClassRules"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
        />

        <!-- Footer -->
        <div class="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center space-x-4 text-gray-600">
              <span>Showing {{ displayedRowCount }} of {{ stats.totalPatients }} patients</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Patient Drawer -->
      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          class="flex flex-col overflow-hidden transition-all duration-300"
          style="width: 50%; height: calc(100vh - 200px)"
        >
          <Patient_Drawer
            ref="patientDrawer"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatientData"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="['details', 'utilization', 'gaps', 'engagement', 'care_team']"
            :default-tab="'gaps'"
            :show-header="true"
            @show-message="showMessage"
            @task-updated="softRefreshGrid"
            @close="closeDetail"
          />
        </div>
      </transition>
    </div>

    <!-- Success/Error Messages -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['fas', message.type === 'success' ? 'check-circle' : 'exclamation-circle']"
            class="mr-2 mt-0.5"
          />
          <div>
            <div class="font-medium">{{ message.text }}</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-enterprise';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import Quality_PatientCell from './cells/Quality_PatientCell.vue';
import Quality_EntityCell from './cells/Quality_EntityCell.vue';
import CC_Work_Summary_Column from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Work_Summary_Column.vue';
import CC_Payer_Composite_Renderer from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Payer_Composite_Renderer.vue';
import MedAdherence_MedicationsCell from './cells/MedAdherence_MedicationsCell.vue';
import MedAdherence_UrgencyCell from './cells/MedAdherence_UrgencyCell.vue';
import MedAdherence_PharmacyCell from './cells/MedAdherence_PharmacyCell.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { getFilterOptions, getPatientList } from '@/services/api/qualityPatientList';


export default {
  name: 'MedAdherence_Patients_List',
  components: {
    AgGridVue,
    FontAwesomeIcon,
    Patient_Drawer,
    Worklist_FilterBar,
    SimpleMultiSelect,
    Quality_PatientCell,
    CC_Work_Summary_Column,
    MedAdherence_MedicationsCell,
    MedAdherence_UrgencyCell,
    MedAdherence_PharmacyCell,
    ExportMenu,
  },
  data() {
    return {
      gridApi: null,
      loading: false,
      tableKey: 0,
      selectedPatient: null,
      message: null,
      displayedRowCount: 0,

      // Entity filters from Worklist_FilterBar
      entityFilters: null,
      myPractices: false,

      // Patient search fields
      searchLastName: undefined,
      searchFirstName: undefined,
      searchDob: undefined,
      searchMbi: undefined,

      // Pharmacy filter
      selectedPharmacies: [],
      allPharmacyNames: [],

      favoritesFilterActive: false,
      showSnoozed: false,

      // Archive view: show expired patients (dropped off the payer's latest file)
      showExpired: false,

      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false
      },
      fullColumnDefs: [
        {
          headerName: '',
          field: 'is_favorited',
          width: 40,
          maxWidth: 40,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: params => {
            if (!params.data) return '';
            const isFav = params.data.is_favorited;
            return `<span class="favorite-star text-base ${isFav ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}" style="cursor:pointer">★</span>`;
          },
        },
        {
          headerName: 'Patient',
          field: 'patient_info',
          cellRenderer: 'Quality_PatientCell',
          cellStyle: { overflow: 'hidden' },
          cellClass: 'cell-theme-left-aligned',
          flex: 1,
          minWidth: 130
        },
        {
          headerName: 'Practice',
          field: 'practice_name',
          cellRenderer: 'Quality_EntityCell',
          cellStyle: { overflow: 'hidden' },
          cellClass: 'cell-theme-left-aligned',
          flex: 0.8,
          minWidth: 100,
          tooltipValueGetter: params => {
            let p = params.data?.practice_name || '';
            let v = params.data?.provider_name || '';
            // ag-grid renders tooltips in a detached popup the DOM spoof engine
            // never sees, so spoof practice/provider names here in Demo Mode.
            if (isDemoMode()) {
              p = p ? spoofText(p) : p;
              v = v ? spoofText(v) : v;
            }
            return p && v ? `${p} / ${v}` : p || v;
          }
        },
        {
          headerName: 'Payer',
          field: 'payer_name',
          cellRenderer: 'CC_Payer_Composite_Renderer',
          cellStyle: { overflow: 'hidden' },
          cellClass: 'cell-theme-left-aligned',
          flex: 0.8,
          minWidth: 100
        },
        {
          headerName: 'Medications',
          field: 'medications',
          cellRenderer: 'MedAdherence_MedicationsCell',
          cellClass: 'cell-theme-left-aligned',
          flex: 2.5,
          minWidth: 300,
          autoHeight: true
        },
        {
          headerName: 'Urgency',
          field: 'medications',
          cellRenderer: 'MedAdherence_UrgencyCell',
          cellClass: 'cell-theme-left-aligned',
          flex: 1,
          minWidth: 120,
          autoHeight: true
        },
        {
          headerName: 'Pharmacy',
          field: 'pharmacy_name',
          cellRenderer: 'MedAdherence_PharmacyCell',
          cellClass: 'cell-theme-left-aligned',
          flex: 0.8,
          minWidth: 100
        },
        {
          headerName: 'Work',
          field: 'work_completed',
          cellRenderer: 'CC_Work_Summary_Column',
          cellClass: 'cell-theme-left-aligned',
          flex: 1,
          minWidth: 120,
          autoHeight: true
        },
      ],
      gridOptions: {
        rowModelType: 'serverSide',
        suppressHorizontalScroll: false,
        animateRows: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        suppressColumnVirtualisation: true,
        suppressSizeToFit: false,
        tooltipShowDelay: 300,
        components: {
          Quality_PatientCell,
          Quality_EntityCell,
          CC_Payer_Composite_Renderer,
          CC_Work_Summary_Column,
          MedAdherence_MedicationsCell,
          MedAdherence_UrgencyCell,
          MedAdherence_PharmacyCell
        },
        autoSizeStrategy: {
          type: 'fitCellContents'
        }
      },
      rowClassRules: {
        'bg-blue-50': params => params.data?.patient_identifier === this.selectedPatient?.patient_identifier
      },
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: '14px',
        headerBackgroundColor: '#f9fafb',
        headerFontSize: '12px',
        headerFontWeight: 600,
        oddRowBackgroundColor: '#ffffff',
        rowBorder: true,
        rowHoverColor: '#f3f4f6',
        spacing: 8,
        wrapperBorder: false
      }),
      stats: {
        totalPatients: 0,
        openGaps: 0,
        overdueGaps: 0
      }
    };
  },
  computed: {
    pharmacyOptions() {
      return this.allPharmacyNames.map(n => ({ value: n, label: n }));
    },
    computedColumnDefs() {
      if (this.selectedPatient) return this.drawerColumnDefs;
      return this.fullColumnDefs;
    },
    drawerColumnDefs() {
      return this.fullColumnDefs.filter(col =>
        col.field !== 'work_completed' && col.field !== 'pharmacy_name'
      );
    },
    selectedPatientData() {
      if (!this.selectedPatient) return null;
      return {
        bene_first_name: this.selectedPatient.bene_first_name,
        bene_last_name: this.selectedPatient.bene_last_name,
        bene_dob: this.selectedPatient.bene_dob,
        bene_sex_cd: this.selectedPatient.bene_sex_cd,
        bene_age: this.selectedPatient.bene_age,
        bene_death_dt: this.selectedPatient.bene_death_dt,
        bene_phone: this.selectedPatient.bene_phone,
        bene_address_1: this.selectedPatient.bene_address_1,
        bene_address_2: this.selectedPatient.bene_address_2,
        bene_city: this.selectedPatient.bene_city,
        bene_state: this.selectedPatient.bene_state,
        bene_zip: this.selectedPatient.bene_zip,
        provider_name: this.selectedPatient.provider_name,
        practice_name: this.selectedPatient.practice_name,
        payer_name: this.selectedPatient.payer_name,
        contract_name: this.selectedPatient.contract_name,
      };
    }
  },
  watch: {
    selectedPatient() {
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.computedColumnDefs);
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 350);
      }
    }
  },
  mounted() {
    this.loadPharmacyNames();
  },
  methods: {
    getRowId(params) {
      return params.data.patient_identifier;
    },

    onEntityFilterChanged(filterData) {
      this.entityFilters = filterData?.entity_filters || null;
      this.myPractices = filterData?.my_practices || false;
      this.searchLastName = filterData?.search_last_name;
      this.searchFirstName = filterData?.search_first_name;
      this.searchDob = filterData?.search_dob;
      this.searchMbi = filterData?.search_mbi;
      this.refreshGrid();
    },

    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.setGridOption('context', {
        componentParent: this,
        openMedAdherenceDetail: (patientData, med) => this.openMedAdherenceDetailForMed(patientData, med),
      });

      const datasource = {
        getRows: async (paramsRequest) => {
          try {
            this.loading = true;

            const data = await getPatientList({
              start_row: paramsRequest.request.startRow,
              end_row: paramsRequest.request.endRow,
              entity_filters: this.entityFilters || undefined,
              my_practices: this.myPractices || undefined,
              measure_type: 'medication_adherence',
              gap_status: 'open',
              search_last_name: this.searchLastName || undefined,
              search_first_name: this.searchFirstName || undefined,
              search_dob: this.searchDob || undefined,
              search_mbi: this.searchMbi || undefined,
              pharmacy_names: this.selectedPharmacies.length ? this.selectedPharmacies : undefined,
              favorites_only: this.favoritesFilterActive || undefined,
              include_expired: this.showExpired || undefined,
              show_snoozed: this.showSnoozed || undefined,
              sort_model: paramsRequest.request.sortModel?.length ? paramsRequest.request.sortModel : undefined,
            });

            if (data?.success) {
              this.stats = data.stats || {
                totalPatients: data.lastRow,
                openGaps: 0,
                overdueGaps: 0
              };
              this.displayedRowCount = data.rowData?.length || 0;

              paramsRequest.success({
                rowData: data.rowData || [],
                lastRow: data.lastRow || 0
              });
            } else {
              paramsRequest.success({ rowData: [], lastRow: 0 });
            }

            setTimeout(() => {
              this.gridApi.sizeColumnsToFit();
            }, 100);
          } catch (error) {
            console.error('Error fetching med-adherence patients:', error);
            paramsRequest.fail();
          } finally {
            this.loading = false;
          }
        }
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);

      window.addEventListener('resize', () => {
        this.gridApi.sizeColumnsToFit();
      });
    },

    refreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },

    softRefreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: false });
      }
    },

    async loadPharmacyNames() {
      try {
        const data = await getFilterOptions();
        if (data?.pharmacy_names) {
          this.allPharmacyNames = data.pharmacy_names;
        }
      } catch (e) {
        // Filter still works without options
      }
    },

    applyPharmacyFilter() {
      this.refreshGrid();
    },

    onRowClicked(event) {
      if (event.event?.target?.closest('.favorite-star') || event.column?.colId === 'is_favorited') {
        this.toggleFavorite(event.data);
        return;
      }
      this.selectedPatient = event.data;
      if (this.gridApi) {
        this.gridApi.redrawRows();
      }
    },

    toggleShowSnoozed() {
      this.showSnoozed = !this.showSnoozed;
      this.refreshGrid();
    },
    toggleShowExpired() {
      this.showExpired = !this.showExpired;
      this.refreshGrid();
    },
    toggleFavoritesFilter() {
      this.favoritesFilterActive = !this.favoritesFilterActive;
      this.refreshGrid();
    },

    async toggleFavorite(rowData) {
      const patientId = rowData.patient_identifier;
      try {
        const { data } = await this.$axios.post('/api/quality/toggle-favorite/', {
          patient_identifier: patientId,
          opportunity_id: rowData.first_opportunity_id,
        });
        rowData.is_favorited = data.favorited;
        const rowNode = this.gridApi.getRowNode(patientId);
        if (rowNode) {
          rowNode.setData({ ...rowData });
        }
      } catch (e) {
        console.error('Failed to toggle favorite', e);
      }
    },

    openMedAdherenceDetailForMed(patientData, med) {
      this.selectedPatient = patientData;
      this.$nextTick(() => {
        if (this.$refs.patientDrawer) {
          this.$refs.patientDrawer.openGap({
            opportunity_id: med.opportunity_id,
            metric_id: med.metric_id,
            measure_name: med.measure_name || med.measure_code,
            measure_code: med.measure_code,
            status: 'open',
            task_type_id: 14,
            task_id: patientData.task_id,
            entity_id: patientData.entity_id,
            metadata: med,
            // Read by Quality_Gap_Detail's Pharmacy Activity section. Carried
            // explicitly because this grid hand-builds the gap object rather
            // than using the /patient-gaps/ payload.
            surescripts_fill: med.surescripts_fill || null,
            patient_identifier: patientData.patient_identifier,
            opportunity: { id: med.opportunity_id, entity: patientData.entity_id },
          });
        }
      });
    },

    closeDetail() {
      this.selectedPatient = null;
      if (this.gridApi) {
        this.gridApi.redrawRows();
        this.gridApi.setGridOption('columnDefs', this.computedColumnDefs);
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 350);
      }
    },

    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => {
        this.message = null;
      }, 5000);
    },

    exportData() {
      if (this.$root?.isDemoMode) return; // Demo Mode: block raw-data export
      this.gridApi.exportDataAsCsv({
        fileName: `med_adherence_patients_${new Date().toISOString().split('T')[0]}.csv`
      });
    }
  }
};
</script>

<style scoped>
/* Custom styles if needed */
</style>
